import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { DashboardStats, SuperAdminDashboardStats } from '../types';
import { useStrictModeGuard } from '../hooks/useStrictModeGuard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDollarSign, faChartLine, faUsers, faMobile, faChartBar, faCrown, faClock, faList, faShoppingCart } from '@fortawesome/free-solid-svg-icons';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { executeOnce } = useStrictModeGuard();
  const [stats, setStats] = useState<DashboardStats>({
    total_users: 0,
    active_users: 0,
    super_admins: 0,
    total_organizations: 0,
    total_applications: 0,
    organizations_with_apps: []
  });

  const fetchStats = useCallback(async (): Promise<void> => {
    try {
      const response = await axios.get<DashboardStats>('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  }, []);

  useEffect(() => {
    executeOnce(() => {
      fetchStats();
    });
  }, [fetchStats, executeOnce]);

  const isOrganizationUser = user?.role === 'organization_admin' || user?.role === 'organization_user';
  const isSuperAdmin = user?.role === 'super_admin';
  const orgStats = stats.organization_stats;
  const superAdminStats = stats.super_admin_stats;

  return (
    <div>
      {/* Welcome Banner */}
      <div className="welcome-banner">
        <h1 className="welcome-title">Welcome to your Dashboard</h1>
        <p className="welcome-subtitle">
          {isOrganizationUser 
            ? "Here's what's happening with your organization today."
            : isSuperAdmin
            ? "Here's what's happening with your SaaS platform today."
            : "Here's what's happening with your SaaS platform today."
          }
        </p>
      </div>

      {/* Key Metrics */}
      <div className="metrics-grid">
        {isOrganizationUser && orgStats ? (
          // Organization user metrics
          <>
            <div className="metric-card">
              <div className="metric-trend trend-up">
                ↑ +{((orgStats.month_revenue / Math.max(orgStats.total_revenue, 1)) * 100).toFixed(1)}%
              </div>
              <div className="metric-icon" style={{ backgroundColor: '#dcfce7', color: '#16a34a' }}>
                <FontAwesomeIcon icon={faDollarSign} />
              </div>
              <div className="metric-value">${orgStats.total_revenue.toFixed(0)}</div>
              <div className="metric-label">Total Revenue</div>
            </div>

            <div className="metric-card">
              <div className="metric-trend trend-up">
                ↑ +{((orgStats.month_revenue / Math.max(orgStats.total_revenue, 1)) * 100).toFixed(1)}%
              </div>
              <div className="metric-icon" style={{ backgroundColor: '#e0f2fe', color: '#0ea5e9' }}>
                <FontAwesomeIcon icon={faDollarSign} />
              </div>
              <div className="metric-value">${orgStats.month_revenue.toFixed(0)}</div>
              <div className="metric-label">This Month</div>
            </div>

            <div className="metric-card">
              <div className="metric-trend trend-up">
                ↑ +{((orgStats.month_subscriptions / Math.max(orgStats.total_subscriptions, 1)) * 100).toFixed(1)}%
              </div>
              <div className="metric-icon" style={{ backgroundColor: '#f3e8ff', color: '#a855f7' }}>
                <FontAwesomeIcon icon={faShoppingCart} />
              </div>
              <div className="metric-value">{orgStats.total_subscriptions}</div>
              <div className="metric-label">Total Subscriptions</div>
            </div>

            <div className="metric-card">
              <div className="metric-trend trend-up">
                ↑ +{((orgStats.month_subscriptions / Math.max(orgStats.total_subscriptions, 1)) * 100).toFixed(1)}%
              </div>
              <div className="metric-icon" style={{ backgroundColor: '#fff7ed', color: '#f97316' }}>
                <FontAwesomeIcon icon={faChartLine} />
              </div>
              <div className="metric-value">{orgStats.month_subscriptions}</div>
              <div className="metric-label">This Month</div>
            </div>
          </>
        ) : isSuperAdmin && superAdminStats ? (
          // Super admin metrics
          <>
            <div className="metric-card">
              <div className="metric-trend trend-up">
                ↑ +{((superAdminStats.month_revenue / Math.max(superAdminStats.total_revenue, 1)) * 100).toFixed(1)}%
              </div>
              <div className="metric-icon" style={{ backgroundColor: '#dcfce7', color: '#16a34a' }}>
                <FontAwesomeIcon icon={faDollarSign} />
              </div>
              <div className="metric-value">${superAdminStats.total_revenue.toFixed(0)}</div>
              <div className="metric-label">Total Revenue</div>
            </div>

            <div className="metric-card">
              <div className="metric-trend trend-up">
                ↑ +{((superAdminStats.month_revenue / Math.max(superAdminStats.total_revenue, 1)) * 100).toFixed(1)}%
              </div>
              <div className="metric-icon" style={{ backgroundColor: '#e0f2fe', color: '#0ea5e9' }}>
                <FontAwesomeIcon icon={faDollarSign} />
              </div>
              <div className="metric-value">${superAdminStats.month_revenue.toFixed(0)}</div>
              <div className="metric-label">This Month</div>
            </div>

            <div className="metric-card">
              <div className="metric-trend trend-up">
                ↑ +{((superAdminStats.total_subscriptions / Math.max(superAdminStats.total_subscriptions, 1)) * 100).toFixed(1)}%
              </div>
              <div className="metric-icon" style={{ backgroundColor: '#f3e8ff', color: '#a855f7' }}>
                <FontAwesomeIcon icon={faShoppingCart} />
              </div>
              <div className="metric-value">{superAdminStats.total_subscriptions}</div>
              <div className="metric-label">Total Subscriptions</div>
            </div>

            <div className="metric-card">
              <div className="metric-trend trend-up">
                ↑ +{((superAdminStats.total_applications / Math.max(superAdminStats.total_applications, 1)) * 100).toFixed(1)}%
              </div>
              <div className="metric-icon" style={{ backgroundColor: '#fff7ed', color: '#f97316' }}>
                <FontAwesomeIcon icon={faMobile} />
              </div>
              <div className="metric-value">{superAdminStats.total_applications}</div>
              <div className="metric-label">Applications</div>
            </div>
          </>
        ) : (
          // Fallback metrics for other user types
          <>
            <div className="metric-card">
              <div className="metric-trend trend-up">
                ↑ +12.5%
              </div>
              <div className="metric-icon" style={{ backgroundColor: '#dcfce7', color: '#16a34a' }}>
                <FontAwesomeIcon icon={faDollarSign} />
              </div>
              <div className="metric-value">{stats.total_users}</div>
              <div className="metric-label">Total Users</div>
            </div>

            <div className="metric-card">
              <div className="metric-trend trend-up">
                ↑ +8.2%
              </div>
              <div className="metric-icon" style={{ backgroundColor: '#e0f2fe', color: '#0ea5e9' }}>
                <FontAwesomeIcon icon={faChartLine} />
              </div>
              <div className="metric-value">{stats.active_users}</div>
              <div className="metric-label">Active Users</div>
            </div>

            <div className="metric-card">
              <div className="metric-trend trend-up">
                ↑ +15.3%
              </div>
              <div className="metric-icon" style={{ backgroundColor: '#f3e8ff', color: '#a855f7' }}>
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <div className="metric-value">{stats.total_organizations}</div>
              <div className="metric-label">Organizations</div>
            </div>

            <div className="metric-card">
              <div className="metric-trend trend-down">
                ↓ -2.1%
              </div>
              <div className="metric-icon" style={{ backgroundColor: '#fff7ed', color: '#f97316' }}>
                <FontAwesomeIcon icon={faMobile} />
              </div>
              <div className="metric-value">{stats.total_applications}</div>
              <div className="metric-label">Applications</div>
            </div>
          </>
        )}
      </div>

      {/* Dashboard Sections */}
      <div className="dashboard-grid">
        {isOrganizationUser && orgStats ? (
          // Organization user sections
          <>
            <div className="dashboard-section">
              <div className="section-header">
                <div className="section-icon" style={{ color: '#3b82f6' }}>
                  <FontAwesomeIcon icon={faMobile} />
                </div>
                <h3 className="section-title">Applications</h3>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
                  {orgStats.total_applications}
                </div>
                <div style={{ color: '#6b7280', fontSize: '14px' }}>
                  Total applications in your organization
                </div>
              </div>
            </div>

            <div className="dashboard-section">
              <div className="section-header">
                <div className="section-icon" style={{ color: '#a855f7' }}>
                  <FontAwesomeIcon icon={faList} />
                </div>
                <h3 className="section-title">Plans</h3>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
                  {orgStats.total_plans}
                </div>
                <div style={{ color: '#6b7280', fontSize: '14px' }}>
                  Total plans across all applications
                </div>
              </div>
            </div>

            <div className="dashboard-section">
              <div className="section-header">
                <div className="section-icon" style={{ color: '#0ea5e9' }}>
                  <FontAwesomeIcon icon={faShoppingCart} />
                </div>
                <h3 className="section-title">Subscriptions</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>Total</span>
                  <span style={{ fontWeight: '600', color: '#1f2937' }}>{orgStats.total_subscriptions}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>This Month</span>
                  <span style={{ fontWeight: '600', color: '#059669' }}>{orgStats.month_subscriptions}</span>
                </div>
              </div>
            </div>
          </>
        ) : isSuperAdmin && superAdminStats ? (
          // Super admin sections
          <>
            <div className="dashboard-section">
              <div className="section-header">
                <div className="section-icon" style={{ color: '#3b82f6' }}>
                  <FontAwesomeIcon icon={faUsers} />
                </div>
                <h3 className="section-title">Organizations</h3>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
                  {superAdminStats.total_organizations}
                </div>
                <div style={{ color: '#6b7280', fontSize: '14px' }}>
                  Total organizations on the platform
                </div>
              </div>
            </div>

            <div className="dashboard-section">
              <div className="section-header">
                <div className="section-icon" style={{ color: '#a855f7' }}>
                  <FontAwesomeIcon icon={faMobile} />
                </div>
                <h3 className="section-title">Applications</h3>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
                  {superAdminStats.total_applications}
                </div>
                <div style={{ color: '#6b7280', fontSize: '14px' }}>
                  Total applications across all organizations
                </div>
              </div>
            </div>

            <div className="dashboard-section">
              <div className="section-header">
                <div className="section-icon" style={{ color: '#0ea5e9' }}>
                  <FontAwesomeIcon icon={faShoppingCart} />
                </div>
                <h3 className="section-title">Subscriptions</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>Total</span>
                  <span style={{ fontWeight: '600', color: '#1f2937' }}>{superAdminStats.total_subscriptions}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>This Month</span>
                  <span style={{ fontWeight: '600', color: '#059669' }}>{Math.floor(superAdminStats.month_revenue / 10)}</span>
                </div>
              </div>
            </div>

            <div className="dashboard-section">
              <div className="section-header">
                <div className="section-icon" style={{ color: '#16a34a' }}>
                  <FontAwesomeIcon icon={faDollarSign} />
                </div>
                <h3 className="section-title">Revenue</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>Total</span>
                  <span style={{ fontWeight: '600', color: '#1f2937' }}>${superAdminStats.total_revenue.toFixed(0)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>This Month</span>
                  <span style={{ fontWeight: '600', color: '#059669' }}>${superAdminStats.month_revenue.toFixed(0)}</span>
                </div>
              </div>
            </div>
          </>
        ) : (
          // Fallback sections for other user types
          <>
            <div className="dashboard-section">
              <div className="section-header">
                <div className="section-icon" style={{ color: '#3b82f6' }}>
                  <FontAwesomeIcon icon={faChartBar} />
                </div>
                <h3 className="section-title">Top Performing Apps</h3>
              </div>
              <div style={{ color: '#6b7280', fontSize: '14px' }}>
                Coming soon - View your most popular applications
              </div>
            </div>

            <div className="dashboard-section">
              <div className="section-header">
                <div className="section-icon" style={{ color: '#a855f7' }}>
                  <FontAwesomeIcon icon={faCrown} />
                </div>
                <h3 className="section-title">Organizations & Apps</h3>
              </div>
              {stats.organizations_with_apps.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {stats.organizations_with_apps.slice(0, 5).map((org) => (
                    <div key={org.id} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '8px 0',
                      borderBottom: '1px solid #f3f4f6'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: '#a855f7'
                        }}></div>
                        <span style={{ fontWeight: '500', color: '#1f2937' }}>{org.name}</span>
                      </div>
                      <div style={{ 
                        backgroundColor: '#f3e8ff', 
                        color: '#a855f7', 
                        padding: '2px 8px', 
                        borderRadius: '12px', 
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {org.application_count} app{org.application_count !== 1 ? 's' : ''}
                      </div>
                    </div>
                  ))}
                  {stats.organizations_with_apps.length > 5 && (
                    <div style={{ 
                      color: '#6b7280', 
                      fontSize: '12px', 
                      textAlign: 'center',
                      padding: '8px 0'
                    }}>
                      +{stats.organizations_with_apps.length - 5} more organizations
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ color: '#6b7280', fontSize: '14px' }}>
                  No organizations found
                </div>
              )}
            </div>

            <div className="dashboard-section">
              <div className="section-header">
                <div className="section-icon" style={{ color: '#0ea5e9' }}>
                  <FontAwesomeIcon icon={faClock} />
                </div>
                <h3 className="section-title">Recent Activity</h3>
              </div>
              <div style={{ color: '#6b7280', fontSize: '14px' }}>
                Coming soon - Track system activity and changes
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
